'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User, Globe, Calendar, Loader2, ArrowRight, Shield } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface RegisterFormData {
  fullName: string;
  email: string;
  password: string;
  dob: string;
  country: string;
  responsiblePlay: boolean;
}

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

const countries = ['India', 'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'UAE', 'Singapore', 'Japan'];

export default function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const { register, handleSubmit, formState: { errors }, watch } = useForm<RegisterFormData>();
  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    // Backend integration point: POST /api/auth/register
    await new Promise((r) => setTimeout(r, 1400));
    toast.success('Account created! Welcome to Brain Basket 🎉');
    router.push('/user-dashboard');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-1">Join Brain Basket</h1>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Create your free account and start competing</p>
      </div>

      {/* Full Name */}
      <div>
        <label className="block text-sm font-semibold mb-1.5 text-foreground" htmlFor="reg-name">Full Name</label>
        <div className="relative">
          <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
          <input
            id="reg-name"
            type="text"
            placeholder="Your full name"
            className="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg outline-none transition-all"
            style={{ background: 'var(--input)', border: `1px solid ${errors.fullName ? '#F43F5E' : 'var(--border)'}`, color: 'var(--foreground)' }}
            onFocus={(e) => { if (!errors.fullName) e.target.style.borderColor = 'var(--primary)'; }}
            onBlur={(e) => { if (!errors.fullName) e.target.style.borderColor = 'var(--border)'; }}
            {...register('fullName', { required: 'Full name is required', minLength: { value: 2, message: 'Name must be at least 2 characters' } })}
          />
        </div>
        {errors.fullName && <p className="text-xs mt-1" style={{ color: '#F43F5E' }}>{errors.fullName.message}</p>}
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-semibold mb-1.5 text-foreground" htmlFor="reg-email">Email Address</label>
        <div className="relative">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
          <input
            id="reg-email"
            type="email"
            placeholder="your@email.com"
            className="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg outline-none transition-all"
            style={{ background: 'var(--input)', border: `1px solid ${errors.email ? '#F43F5E' : 'var(--border)'}`, color: 'var(--foreground)' }}
            onFocus={(e) => { if (!errors.email) e.target.style.borderColor = 'var(--primary)'; }}
            onBlur={(e) => { if (!errors.email) e.target.style.borderColor = 'var(--border)'; }}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })}
          />
        </div>
        {errors.email && <p className="text-xs mt-1" style={{ color: '#F43F5E' }}>{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div>
        <label className="block text-sm font-semibold mb-1.5 text-foreground" htmlFor="reg-password">Password</label>
        <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Minimum 8 characters with at least one number</p>
        <div className="relative">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
          <input
            id="reg-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Create a strong password"
            className="w-full pl-10 pr-10 py-2.5 text-sm rounded-lg outline-none transition-all"
            style={{ background: 'var(--input)', border: `1px solid ${errors.password ? '#F43F5E' : 'var(--border)'}`, color: 'var(--foreground)' }}
            onFocus={(e) => { if (!errors.password) e.target.style.borderColor = 'var(--primary)'; }}
            onBlur={(e) => { if (!errors.password) e.target.style.borderColor = 'var(--border)'; }}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
              pattern: { value: /(?=.*\d)/, message: 'Password must include at least one number' },
            })}
          />
          <button type="button" onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2" aria-label="Toggle password">
            {showPassword ? <EyeOff size={16} style={{ color: 'var(--muted-foreground)' }} /> : <Eye size={16} style={{ color: 'var(--muted-foreground)' }} />}
          </button>
        </div>
        {errors.password && <p className="text-xs mt-1" style={{ color: '#F43F5E' }}>{errors.password.message}</p>}
      </div>

      {/* DOB + Country row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold mb-1.5 text-foreground" htmlFor="reg-dob">Date of Birth</label>
          <div className="relative">
            <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <input
              id="reg-dob"
              type="date"
              className="w-full pl-10 pr-2 py-2.5 text-sm rounded-lg outline-none transition-all"
              style={{ background: 'var(--input)', border: `1px solid ${errors.dob ? '#F43F5E' : 'var(--border)'}`, color: 'var(--foreground)' }}
              onFocus={(e) => { if (!errors.dob) e.target.style.borderColor = 'var(--primary)'; }}
              onBlur={(e) => { if (!errors.dob) e.target.style.borderColor = 'var(--border)'; }}
              {...register('dob', { required: 'Date of birth is required' })}
            />
          </div>
          {errors.dob && <p className="text-xs mt-1" style={{ color: '#F43F5E' }}>{errors.dob.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1.5 text-foreground" htmlFor="reg-country">Country</label>
          <div className="relative">
            <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <select
              id="reg-country"
              className="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg outline-none transition-all appearance-none"
              style={{ background: 'var(--input)', border: `1px solid ${errors.country ? '#F43F5E' : 'var(--border)'}`, color: 'var(--foreground)' }}
              {...register('country', { required: 'Select your country' })}
            >
              <option value="">Select</option>
              {countries.map((c) => (
                <option key={`country-${c}`} value={c}>{c}</option>
              ))}
            </select>
          </div>
          {errors.country && <p className="text-xs mt-1" style={{ color: '#F43F5E' }}>{errors.country.message}</p>}
        </div>
      </div>

      {/* Responsible play */}
      <div className="rounded-xl p-4" style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
        <div className="flex items-start gap-3">
          <Shield size={16} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '2px' }} />
          <div className="flex-1">
            <p className="text-xs font-semibold mb-1" style={{ color: '#F59E0B' }}>Responsible Play Acknowledgement</p>
            <p className="text-xs leading-relaxed mb-3" style={{ color: 'var(--muted-foreground)' }}>
              Paid competitions are only available to users 18+. Participation involves skill, but winning is not guaranteed. Set spending limits to stay in control.
            </p>
            <div className="flex items-start gap-2">
              <input
                id="responsible-play"
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-primary"
                {...register('responsiblePlay', { required: 'You must acknowledge the responsible play notice' })}
              />
              <label htmlFor="responsible-play" className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                I confirm I am 18+ (or legal age in my region), have read the Terms of Service and Responsible Play policy.
              </label>
            </div>
            {errors.responsiblePlay && <p className="text-xs mt-1" style={{ color: '#F43F5E' }}>{errors.responsiblePlay.message}</p>}
          </div>
        </div>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full justify-center py-3 text-base"
        style={{ opacity: isLoading ? 0.8 : 1 }}
      >
        {isLoading ? (
          <><Loader2 size={18} className="animate-spin" /> Creating account...</>
        ) : (
          <>Create Free Account <ArrowRight size={18} /></>
        )}
      </button>

      <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="font-semibold" style={{ color: '#A78BFA' }}>Sign in</button>
      </p>
    </form>
  );
}